import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';

import PriceRangeSlider from '../../../components/PriceRangeSlider';
import i18n from '../../../i18n/i18n';

import { ReactComponent as CommercialIcon } from "../../../images/mapSearch/commercial-icon.svg";
import { ReactComponent as EvIcon } from "../../../images/mapSearch/ev-icon.svg";
import { ReactComponent as HotIcon } from "../../../images/mapSearch/hot-icon.svg";
import { ReactComponent as ResidentialIcon } from "../../../images/mapSearch/residential-icon.svg";
import { ReactComponent as ReservedIcon } from '../../../images/icons/reserve-filter-icon.svg';

const initialFilter = {
  filterEvCharging: false,
  filterHot: false,
  filterReserved: false,
  filterLanded: false,
  filterApartment: false,
  filterCommercial: false,
  minPrice: 0,
  maxPrice: 10000,
};

const FilterModal = ({
  isModalOpen,
  onCloseModal,
  onApplyFilter,
  onResetFilter,
}) => {
  const [filter, setFilter] = useState(localStorage.parkingFilter ? JSON.parse(localStorage.parkingFilter) : initialFilter);

  const onChangeFilter = (key) => {
    setFilter((prevState) => ({
      ...prevState,
      [key]: !prevState[key]
    }));
  };

  const onChangePriceRange = ([min, max]) => {
    setFilter((prevState) => ({
      ...prevState,
      minPrice: min,
      maxPrice: max,
    }));
  }

  const resetFilter = () => {
    localStorage.removeItem("parkingFilter");
    setFilter(initialFilter);
    onResetFilter();
  };

  const applyFilter = () => {
    if (filter === initialFilter) {
      localStorage.removeItem("parkingFilter");
    } else {
      localStorage.setItem("parkingFilter", JSON.stringify(filter));
    }
    onApplyFilter();
    onCloseModal();
  };

  return (
    <div tabIndex="-1" aria-hidden="true" className={`fixed top-0 left-0 right-0 bottom-0 z-50 w-full p-4 overflow-x-hidden overflow-y-auto max-h-screen ${isModalOpen ? '' : 'hidden'}`}>
      <div className="flex items-center justify-center min-h-screen text-center max-w-[80%] md:max-w-[500px] mx-auto">
        <div className="fixed inset-0 transition-opacity">
          <div className="absolute inset-0 bg-gray-900 opacity-75" />
        </div>
        <div className="relative bg-white z-20 rounded-lg shadow">
          <div className="flex items-center justify-between p-4 border-b rounded-t">
            <h3 className="text-xl font-semibold text-gray-900">{i18n.t('findParking.filter')}</h3>
            <button type="button" className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center" onClick={onCloseModal}>
              <FontAwesomeIcon icon={faClose} />
            </button>
          </div>
          <div className="p-10 min-w-[500px]">
            <div className="flex gap-5">
              <button onClick={() => onChangeFilter("filterEvCharging")} className={`border border-[#CCCCCC] rounded-[31px] h-[50px] w-full py-5 text-center flex items-center justify-center gap-2 ${filter.filterEvCharging ? 'bg-primary text-white' : 'bg-transparent text-black'}`}>
                <EvIcon className={`h-[18px] ${filter.filterEvCharging ? 'text-white' : 'text-[#bfbfbf]'}`} />
                <span>{i18n.t('findParking.filterFields.ev')}</span>
              </button>
              <button onClick={() => onChangeFilter("filterHot")} className={`border border-[#CCCCCC] rounded-[31px] h-[50px] w-full py-5 text-center flex items-center justify-center gap-2 ${filter.filterHot ? 'bg-primary text-white' : 'bg-transparent text-black'}`} >
                <HotIcon className={`h-[18px] ${filter.filterHot ? 'text-white' : 'text-[#bfbfbf]'}`} />
                <span>{i18n.t('findParking.filterFields.hot')}</span>
              </button>
            </div>
            <div className="flex flex-col mt-7 pt-8 w-full border-t border-gray-200">
              <p className="text-left mb-8 text-sm font-medium">{i18n.t('findParking.filterFields.priceRange')}</p>
              <PriceRangeSlider currency={localStorage.lang === 'zh-HK' ? 'HKD' : 'MYR'} onChange={onChangePriceRange} value={[filter.minPrice, filter.maxPrice]} />
            </div>
            <div className="flex flex-col mt-7 pt-8 w-full border-t border-gray-200">
              <p className="text-left mb-4 text-sm font-medium">{i18n.t('findParking.filterFields.typeOfParking')}</p>
              <div className="flex items-center justify-center gap-[21px]">
                <button onClick={() => onChangeFilter("filterReserved")} className={`border border-[#CCCCCC] rounded-[14px] p-5 w-full ${filter.filterReserved ? "bg-primary text-white" : "bg-transparent text-black"}`}>
                  <div className="flex flex-col items-start">
                    <ReservedIcon className={`mt-1 mb-5 h-[18px] ${filter.filterReserved ? 'text-white' : 'text-[#bfbfbf]'}`} />
                    <span className="text-[14px] font-medium">{i18n.t('findParking.carparkType.reserved')}</span>
                  </div>
                </button>
                <button onClick={() => onChangeFilter("filterCommercial")} className={`border border-[#CCCCCC] rounded-[14px] p-5 w-full ${filter.filterCommercial ? "bg-primary text-white" : "bg-transparent text-black"}`}>
                  <div className="flex flex-col items-start">
                    <CommercialIcon className={`mt-1 mb-5 h-[18px] ${filter.filterCommercial ? 'text-white' : 'text-[#bfbfbf]'}`} />
                    <span className="text-[14px] font-medium">{i18n.t('findParking.carparkType.commercial')}</span>
                  </div>
                </button>
                <button onClick={() => onChangeFilter("filterApartment")} className={`border border-[#CCCCCC] rounded-[14px] p-5 w-full ${filter.filterApartment ? "bg-primary text-white" : "bg-transparent text-black"}`}>
                  <div className="flex flex-col items-start">
                    <ResidentialIcon className={`mt-1 mb-5 h-[18px] ${filter.filterApartment ? 'text-white' : 'text-[#bfbfbf]'}`} />
                    <span className="text-[14px] font-medium">{i18n.t('findParking.carparkType.residential')}</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
          <div className="flex justify-between border-t border-gray-200">
            <div className="flex p-6 space-x-2 rounded-b">
              <button type="button" onClick={resetFilter} className="text-black bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none rounded-lg text-sm font-medium px-5 py-2.5">{i18n.t('findParking.filterFields.resetFilters')}</button>
            </div>
            <div className="flex items-center p-6 space-x-2 rounded-b">
              <button type="button" onClick={onCloseModal} className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none rounded-lg text-sm font-medium px-5 py-2.5">{i18n.t('findParking.filterFields.cancel')}</button>
              <button type="button" onClick={applyFilter} className="text-white bg-primary900 hover:bg-primary focus:ring-4 focus:outline-none font-medium rounded-[24px] text-sm px-5 py-2.5 text-center">{i18n.t('findParking.filterFields.applyFilters')}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FilterModal;
